var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventIcon from "@mui/icons-material/Event";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StadiumIcon from "@mui/icons-material/Stadium";
import { Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack, Tooltip, Typography, } from "@mui/material";
import { addDays, addMonths, eachDayOfInterval, endOfDay, endOfMonth, endOfWeek, format, getDay, isBefore, isSameDay, isSameMonth, isWithinInterval, startOfDay, startOfMonth, startOfWeek, subMonths, } from "date-fns";
import React from "react";
// Day name abbreviations (Sunday = 0, Monday = 1, etc.)
var ALL_DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Get day names in order based on week start day
var getDayNames = function (weekStartDay) {
    var names = [];
    for (var i = 0; i < 7; i++) {
        names.push(ALL_DAY_NAMES[(weekStartDay + i) % 7]);
    }
    return names;
};
/**
 * Ensure a value is a Date object. Handles various date formats from form state.
 */
var ensureDate = function (value) {
    if (!value)
        return null;
    if (value instanceof Date)
        return value;
    if (typeof value === "string") {
        // Handle ISO datetime strings (e.g., "2026-02-16T00:00:00.000Z")
        // or simple date strings (e.g., "2026-02-16")
        var dateStr = value.split("T")[0]; // Get just the date part
        var parts = dateStr === null || dateStr === void 0 ? void 0 : dateStr.split("-").map(Number);
        if (isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2]))
            return null;
        return new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
    }
    return null;
};
export var FinalizeStep = function (_a) {
    var _b, _c, _d;
    var teamCount = _a.teamCount, divisionCount = _a.divisionCount, formatRounds = _a.formatRounds, venues = _a.venues, matchTiming = _a.matchTiming, scheduleWindow = _a.scheduleWindow, exclusions = _a.exclusions, onGenerate = _a.onGenerate, isGenerating = _a.isGenerating, generateError = _a.generateError;
    // Normalize exclusion dates to ensure they're Date objects (form state may serialize them as strings)
    var excludedDates = React.useMemo(function () {
        return ((exclusions === null || exclusions === void 0 ? void 0 : exclusions.excludedDates) || [])
            .map(function (ed) { return (__assign(__assign({}, ed), { date: ensureDate(ed.date) })); })
            .filter(function (ed) { return ed.date !== null; });
    }, [exclusions === null || exclusions === void 0 ? void 0 : exclusions.excludedDates]);
    // Normalize blackout periods - ensure full day coverage (start of day to end of day)
    var blackoutPeriods = React.useMemo(function () {
        return ((exclusions === null || exclusions === void 0 ? void 0 : exclusions.blackoutPeriods) || [])
            .map(function (bp) {
            var start = ensureDate(bp.startDate);
            var end = ensureDate(bp.endDate);
            if (!start || !end)
                return null;
            return __assign(__assign({}, bp), { startDate: startOfDay(start), endDate: endOfDay(end) });
        })
            .filter(function (bp) { return bp !== null; });
    }, [exclusions === null || exclusions === void 0 ? void 0 : exclusions.blackoutPeriods]);
    var gameWeekStartDay = (_b = formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.gameWeekStartDay) !== null && _b !== void 0 ? _b : 0;
    var _e = __read(React.useState((formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.startDate) || new Date()), 2), currentMonth = _e[0], setCurrentMonth = _e[1];
    // Calculate season stats
    var gamesPerRound = Math.floor(teamCount / 2);
    var scheduleMode = (formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.scheduleMode) || "roundRobin";
    // For round robin: calculate based on matchesPerPairing
    var roundRobinTotalRounds = ((formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.matchesPerPairing) || 1) *
        (teamCount > 0 ? teamCount - 1 : 0);
    var roundRobinTotalGames = gamesPerRound * roundRobinTotalRounds;
    var startDate = formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.startDate;
    var endDate = formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.endDate;
    // Use timestamps for dependency tracking (Date objects don't compare correctly)
    var startDateTs = startDate === null || startDate === void 0 ? void 0 : startDate.getTime();
    var endDateTs = endDate === null || endDate === void 0 ? void 0 : endDate.getTime();
    // Match duration display
    var matchDuration = (matchTiming === null || matchTiming === void 0 ? void 0 : matchTiming.matchDuration) || 0;
    var warmupTime = (matchTiming === null || matchTiming === void 0 ? void 0 : matchTiming.warmupTime) || 0;
    var totalSlotTime = matchDuration + warmupTime;
    // Venue mode display
    var venueMode = (venues === null || venues === void 0 ? void 0 : venues.venueMode) === "homeAway"
        ? "Home & Away"
        : (venues === null || venues === void 0 ? void 0 : venues.venueMode) === "neutral"
            ? "Neutral Venues"
            : "Not set";
    var pitchCount = ((_c = venues === null || venues === void 0 ? void 0 : venues.selectedPitchIds) === null || _c === void 0 ? void 0 : _c.length) || 0;
    // Get enabled days from pitch schedules (0=Sun, 1=Mon, ..., 6=Sat)
    var enabledDays = React.useMemo(function () {
        var _a;
        var enabled = new Set();
        (_a = scheduleWindow === null || scheduleWindow === void 0 ? void 0 : scheduleWindow.pitchSchedules) === null || _a === void 0 ? void 0 : _a.forEach(function (ps) {
            Object.entries(ps.days).forEach(function (_a) {
                var _b;
                var _c = __read(_a, 2), key = _c[0], config = _c[1];
                if (config.enabled) {
                    var dayMap = {
                        sunday: 0,
                        monday: 1,
                        tuesday: 2,
                        wednesday: 3,
                        thursday: 4,
                        friday: 5,
                        saturday: 6,
                    };
                    enabled.add((_b = dayMap[key]) !== null && _b !== void 0 ? _b : 0);
                }
            });
        });
        return enabled;
    }, [scheduleWindow === null || scheduleWindow === void 0 ? void 0 : scheduleWindow.pitchSchedules]);
    // Check if a date is excluded (single date)
    var isDateExcluded = function (date) {
        return excludedDates.find(function (ed) { return isSameDay(ed.date, date); });
    };
    // Check if a date falls in a whole-week exclusion
    var isInWholeWeekExclusion = function (date) {
        return excludedDates.find(function (ed) {
            if (!ed.wholeWeek)
                return false;
            var weekStartDate = startOfWeek(ed.date, {
                weekStartsOn: gameWeekStartDay,
            });
            var weekEndDate = endOfWeek(ed.date, {
                weekStartsOn: gameWeekStartDay,
            });
            return isWithinInterval(date, { start: weekStartDate, end: weekEndDate });
        });
    };
    // Check if a date is in a blackout period
    var isInBlackout = function (date) {
        return blackoutPeriods.find(function (bp) {
            return isWithinInterval(date, { start: bp.startDate, end: bp.endDate });
        });
    };
    // Helper: Check if ANY day in a week has an exclusion or blackout
    // If so, the entire game week is excluded from scheduling
    var isWeekExcluded = function (weekStart, weekEnd) {
        // Check if any whole-week exclusion matches this week
        var hasWholeWeekExclusion = excludedDates.some(function (ed) {
            if (!ed.wholeWeek)
                return false;
            var edWeekStart = startOfWeek(ed.date, {
                weekStartsOn: gameWeekStartDay,
            });
            return isSameDay(edWeekStart, weekStart);
        });
        if (hasWholeWeekExclusion)
            return true;
        // Check if ANY day in this week overlaps with a blackout period
        var daysInWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });
        return daysInWeek.some(function (day) {
            return blackoutPeriods.some(function (bp) {
                return isWithinInterval(day, { start: bp.startDate, end: bp.endDate });
            });
        });
    };
    // Calculate number of playoff rounds needed
    var playoffRoundsNeeded = React.useMemo(function () {
        if (!(formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.divisionPlayoffs))
            return 0;
        var enabledPlayoffs = formatRounds.divisionPlayoffs.filter(function (p) { return p.enabled; });
        if (enabledPlayoffs.length === 0)
            return 0;
        return Math.max.apply(Math, __spreadArray([], __read(enabledPlayoffs.map(function (p) { return Math.ceil(Math.log2(p.teamCount)); })), false));
    }, [formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.divisionPlayoffs]);
    // Calculate all available weeks between start and end (for fillDates mode)
    var availableWeeks = React.useMemo(function () {
        if (!startDate)
            return [];
        if (scheduleMode === "fillDates" && !endDate)
            return [];
        var weeks = [];
        // GW1 should be the first FULL week starting ON or AFTER the start date
        var currentWeekStart = startOfWeek(startDate, {
            weekStartsOn: gameWeekStartDay,
        });
        // If start date is after the week start, move to the next week
        if (isBefore(currentWeekStart, startDate)) {
            currentWeekStart = addDays(currentWeekStart, 7);
        }
        var effectiveEndDate = scheduleMode === "fillDates" ? endDate : addDays(startDate, 365);
        var maxWeeks = 100;
        var weekCount = 0;
        while (currentWeekStart <= effectiveEndDate && weekCount < maxWeeks) {
            var weekEnd = endOfWeek(currentWeekStart, {
                weekStartsOn: gameWeekStartDay,
            });
            if (!isWeekExcluded(currentWeekStart, weekEnd)) {
                weeks.push({ weekStart: currentWeekStart, weekEnd: weekEnd });
            }
            currentWeekStart = addDays(currentWeekStart, 7);
            weekCount++;
        }
        return weeks;
    }, [
        startDate,
        endDate,
        scheduleMode,
        excludedDates,
        blackoutPeriods,
        startDateTs,
        endDateTs,
        gameWeekStartDay,
    ]);
    // Calculate gameweeks and playoffWeeks together
    var _f = React.useMemo(function () {
        var _a, _b;
        if (!startDate)
            return { gameWeeks: [], playoffWeeks: [], totalGames: 0 };
        // Calculate slots per week
        var slotsPerWeek = 0;
        (_a = scheduleWindow === null || scheduleWindow === void 0 ? void 0 : scheduleWindow.pitchSchedules) === null || _a === void 0 ? void 0 : _a.forEach(function (ps) {
            Object.values(ps.days).forEach(function (day) {
                var _a, _b, _c, _d;
                if (!day.enabled || !day.timeWindows[0])
                    return;
                var tw = day.timeWindows[0];
                var startParts = tw.startTime.split(":").map(Number);
                var endParts = tw.endTime.split(":").map(Number);
                var minutes = ((_a = endParts[0]) !== null && _a !== void 0 ? _a : 0) * 60 +
                    ((_b = endParts[1]) !== null && _b !== void 0 ? _b : 0) -
                    (((_c = startParts[0]) !== null && _c !== void 0 ? _c : 0) * 60 + ((_d = startParts[1]) !== null && _d !== void 0 ? _d : 0));
                var slotDuration = matchTiming
                    ? matchTiming.matchDuration +
                        matchTiming.halfTimeBreak +
                        matchTiming.warmupTime
                    : 45;
                slotsPerWeek += Math.floor(minutes / slotDuration);
            });
        });
        if (slotsPerWeek === 0)
            return { gameWeeks: [], playoffWeeks: [], totalGames: 0 };
        var enabledPlayoffs = ((_b = formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.divisionPlayoffs) === null || _b === void 0 ? void 0 : _b.filter(function (p) { return p.enabled; })) || [];
        if (scheduleMode === "roundRobin") {
            // ROUND ROBIN MODE
            var weeks = [];
            // GW1 should be the first FULL week starting ON or AFTER the start date
            var currentWeekStart = startOfWeek(startDate, {
                weekStartsOn: gameWeekStartDay,
            });
            // If start date is after the week start, move to the next week
            if (isBefore(currentWeekStart, startDate)) {
                currentWeekStart = addDays(currentWeekStart, 7);
            }
            var gamesScheduled = 0;
            var gameWeekNumber = 1;
            var maxWeeks = 100;
            var weekCount = 0;
            while (gamesScheduled < roundRobinTotalGames && weekCount < maxWeeks) {
                var weekEnd = endOfWeek(currentWeekStart, {
                    weekStartsOn: gameWeekStartDay,
                });
                if (!isWeekExcluded(currentWeekStart, weekEnd)) {
                    var gamesThisWeek = Math.min(gamesPerRound, roundRobinTotalGames - gamesScheduled);
                    weeks.push({
                        weekNumber: gameWeekNumber,
                        weekStart: currentWeekStart,
                        weekEnd: weekEnd,
                        gamesCount: gamesThisWeek,
                    });
                    gamesScheduled += gamesThisWeek;
                    gameWeekNumber++;
                }
                currentWeekStart = addDays(currentWeekStart, 7);
                weekCount++;
            }
            // Calculate playoff weeks after regular season
            var playoffs = [];
            if (enabledPlayoffs.length > 0 && weeks.length > 0) {
                var lastGameWeek = weeks[weeks.length - 1];
                currentWeekStart = addDays(lastGameWeek.weekEnd, 1);
                currentWeekStart = startOfWeek(currentWeekStart, {
                    weekStartsOn: gameWeekStartDay,
                });
                if (isWithinInterval(currentWeekStart, {
                    start: lastGameWeek.weekStart,
                    end: lastGameWeek.weekEnd,
                })) {
                    currentWeekStart = addDays(currentWeekStart, 7);
                }
                var playoffRound_1 = 1;
                weekCount = 0;
                var _loop_1 = function () {
                    var weekEnd = endOfWeek(currentWeekStart, {
                        weekStartsOn: gameWeekStartDay,
                    });
                    if (!isWeekExcluded(currentWeekStart, weekEnd)) {
                        var matchCount_1 = 0;
                        enabledPlayoffs.forEach(function (p) {
                            var divRounds = Math.ceil(Math.log2(p.teamCount));
                            var roundFromEnd = divRounds - playoffRound_1 + 1;
                            if (roundFromEnd >= 1) {
                                matchCount_1 += Math.pow(2, roundFromEnd - 1);
                            }
                        });
                        if (matchCount_1 > 0) {
                            playoffs.push({
                                roundNumber: playoffRound_1,
                                weekStart: currentWeekStart,
                                weekEnd: weekEnd,
                                matchCount: matchCount_1,
                            });
                        }
                        playoffRound_1++;
                    }
                    currentWeekStart = addDays(currentWeekStart, 7);
                    weekCount++;
                };
                while (playoffRound_1 <= playoffRoundsNeeded && weekCount < 20) {
                    _loop_1();
                }
            }
            return {
                gameWeeks: weeks,
                playoffWeeks: playoffs,
                totalGames: roundRobinTotalGames,
            };
        }
        else {
            // FILL DATES MODE: Playoffs take priority at the end
            if (availableWeeks.length === 0) {
                return { gameWeeks: [], playoffWeeks: [], totalGames: 0 };
            }
            var playoffs_1 = [];
            var playoffWeeksNeeded = playoffRoundsNeeded;
            var availableForLeague = __spreadArray([], __read(availableWeeks), false);
            if (enabledPlayoffs.length > 0 && playoffWeeksNeeded > 0) {
                var playoffWeekSlots = [];
                var weeksReserved = 0;
                for (var i = availableWeeks.length - 1; i >= 0 && weeksReserved < playoffWeeksNeeded; i--) {
                    var week = availableWeeks[i];
                    if (week) {
                        playoffWeekSlots.unshift(week);
                        weeksReserved++;
                    }
                }
                availableForLeague = availableWeeks.slice(0, availableWeeks.length - weeksReserved);
                playoffWeekSlots.forEach(function (slot, index) {
                    var playoffRound = index + 1;
                    var matchCount = 0;
                    enabledPlayoffs.forEach(function (p) {
                        var divRounds = Math.ceil(Math.log2(p.teamCount));
                        var roundFromEnd = divRounds - playoffRound + 1;
                        if (roundFromEnd >= 1) {
                            matchCount += Math.pow(2, roundFromEnd - 1);
                        }
                    });
                    playoffs_1.push({
                        roundNumber: playoffRound,
                        weekStart: slot.weekStart,
                        weekEnd: slot.weekEnd,
                        matchCount: matchCount,
                    });
                });
            }
            var weeks = availableForLeague.map(function (slot, index) { return ({
                weekNumber: index + 1,
                weekStart: slot.weekStart,
                weekEnd: slot.weekEnd,
                gamesCount: gamesPerRound,
            }); });
            var fillDatesTotalGames = weeks.length * gamesPerRound;
            return {
                gameWeeks: weeks,
                playoffWeeks: playoffs_1,
                totalGames: fillDatesTotalGames,
            };
        }
    }, [
        startDate,
        endDate,
        scheduleMode,
        scheduleWindow,
        matchTiming,
        roundRobinTotalGames,
        roundRobinTotalRounds,
        gamesPerRound,
        excludedDates,
        blackoutPeriods,
        availableWeeks,
        playoffRoundsNeeded,
        formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.divisionPlayoffs,
        startDateTs,
        endDateTs,
    ]), gameWeeks = _f.gameWeeks, playoffWeeks = _f.playoffWeeks, totalGames = _f.totalGames;
    // Get gameweek for a date
    var getGameWeekForDate = function (date) {
        return gameWeeks.find(function (gw) {
            return isWithinInterval(date, { start: gw.weekStart, end: gw.weekEnd });
        });
    };
    // Get playoff week for a date
    var getPlayoffWeekForDate = function (date) {
        return playoffWeeks.find(function (pw) {
            return isWithinInterval(date, { start: pw.weekStart, end: pw.weekEnd });
        });
    };
    // Calendar navigation
    var goToPrevMonth = function () { return setCurrentMonth(subMonths(currentMonth, 1)); };
    var goToNextMonth = function () { return setCurrentMonth(addMonths(currentMonth, 1)); };
    // Generate calendar days for current month
    var calendarDays = React.useMemo(function () {
        var monthStart = startOfMonth(currentMonth);
        var monthEnd = endOfMonth(currentMonth);
        var calStart = startOfWeek(monthStart, {
            weekStartsOn: gameWeekStartDay,
        });
        var calEnd = endOfWeek(monthEnd, { weekStartsOn: gameWeekStartDay });
        return eachDayOfInterval({ start: calStart, end: calEnd });
    }, [currentMonth, gameWeekStartDay]);
    // Estimated end date
    var estimatedEndDate = React.useMemo(function () {
        var _a, _b, _c, _d;
        if (playoffWeeks.length > 0) {
            return (_b = (_a = playoffWeeks[playoffWeeks.length - 1]) === null || _a === void 0 ? void 0 : _a.weekEnd) !== null && _b !== void 0 ? _b : null;
        }
        if (gameWeeks.length > 0) {
            return (_d = (_c = gameWeeks[gameWeeks.length - 1]) === null || _c === void 0 ? void 0 : _c.weekEnd) !== null && _d !== void 0 ? _d : null;
        }
        return null;
    }, [gameWeeks, playoffWeeks]);
    // Total playoff matches
    var totalPlayoffMatches = React.useMemo(function () {
        if (!(formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.divisionPlayoffs))
            return 0;
        return formatRounds.divisionPlayoffs
            .filter(function (p) { return p.enabled; })
            .reduce(function (sum, p) { return sum + (p.teamCount - 1); }, 0);
    }, [formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.divisionPlayoffs]);
    // Validation
    var hasSchedule = (_d = scheduleWindow === null || scheduleWindow === void 0 ? void 0 : scheduleWindow.pitchSchedules) === null || _d === void 0 ? void 0 : _d.some(function (ps) {
        return Object.values(ps.days).some(function (d) { return d.enabled; });
    });
    var isReady = scheduleMode === "manual"
        ? teamCount >= 2 && divisionCount >= 1
        : teamCount >= 2 &&
            divisionCount >= 1 &&
            (formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.matchesPerPairing) &&
            (formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.startDate) &&
            (matchTiming === null || matchTiming === void 0 ? void 0 : matchTiming.matchDuration) &&
            hasSchedule &&
            gameWeeks.length > 0;
    // Get playoff round name
    var getPlayoffRoundName = function (roundNum, totalRounds) {
        var roundsFromEnd = totalRounds - roundNum + 1;
        if (roundsFromEnd === 1)
            return "Final";
        if (roundsFromEnd === 2)
            return "Semi";
        if (roundsFromEnd === 3)
            return "QF";
        return "R".concat(roundNum);
    };
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: { textAlign: "center", mb: 3 }, children: [_jsx(Typography, { variant: "h5", fontWeight: 600, gutterBottom: true, children: scheduleMode === "manual"
                            ? "Activate Season"
                            : "Finalize & Generate" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: scheduleMode === "manual"
                            ? "Review your configuration and activate the season"
                            : "Review your configuration and generate the match schedule" })] }), scheduleMode === "manual" ? (_jsxs(_Fragment, { children: [_jsxs(Paper, { variant: "outlined", sx: { p: 3, mb: 3 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 2 }, children: [_jsx(GroupsIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Season Overview" })] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [teamCount, " teams in ", divisionCount, " division", divisionCount !== 1 ? "s" : ""] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Manual scheduling \u2014 matches will be created individually" })] }), _jsx(Alert, { severity: "info", sx: { mb: 3 }, children: _jsx(Typography, { variant: "body2", children: "The season will be activated with no pre-generated matches. You can create matches individually from the season page." }) })] })) : (_jsxs(_Fragment, { children: [_jsxs(Stack, { direction: { xs: "column", md: "row" }, spacing: 2, sx: { mb: 3 }, children: [_jsxs(Paper, { variant: "outlined", sx: { p: 2, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 1 }, children: [_jsx(GroupsIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Teams & Format" })] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [teamCount, " teams in ", divisionCount, " division", divisionCount !== 1 ? "s" : ""] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: scheduleMode === "fillDates"
                                            ? "Fill dates mode"
                                            : (formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.matchesPerPairing) === 1
                                                ? "Single round-robin"
                                                : "".concat(formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.matchesPerPairing, "x round-robin") })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 1 }, children: [_jsx(StadiumIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Venues" })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: venueMode }), (venues === null || venues === void 0 ? void 0 : venues.venueMode) === "neutral" && (_jsxs(Typography, { variant: "body2", color: "text.secondary", children: [pitchCount, " pitch", pitchCount !== 1 ? "es" : "", " selected"] }))] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 1 }, children: [_jsx(ScheduleIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Match Timing" })] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [matchDuration, " min matches"] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [totalSlotTime, " min slots total"] })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, flex: 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { mb: 1 }, children: [_jsx(EventIcon, { color: "primary", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, children: "Schedule" })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: startDate ? format(startDate, "MMM d, yyyy") : "Not set" }), (excludedDates.length > 0 || blackoutPeriods.length > 0) && (_jsxs(Typography, { variant: "body2", color: "text.secondary", children: [excludedDates.length + blackoutPeriods.length, " exclusion", excludedDates.length + blackoutPeriods.length !== 1
                                                ? "s"
                                                : ""] }))] })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 3, divider: _jsx(Divider, { orientation: "vertical", flexItem: true }), justifyContent: "center", children: [_jsxs(Box, { sx: { textAlign: "center", minWidth: 80 }, children: [_jsx(Typography, { variant: "h4", fontWeight: 700, color: "primary", children: gameWeeks.length + playoffWeeks.length }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "total weeks" })] }), _jsxs(Box, { sx: { textAlign: "center", minWidth: 80 }, children: [_jsx(Typography, { variant: "h4", fontWeight: 700, color: "primary", children: totalGames + totalPlayoffMatches }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "total matches" })] }), _jsxs(Box, { sx: { textAlign: "center", minWidth: 120 }, children: [_jsxs(Typography, { variant: "h6", fontWeight: 600, color: "text.primary", children: [startDate ? format(startDate, "MMM d") : "—", " → ", estimatedEndDate
                                                        ? format(estimatedEndDate, "MMM d, yyyy")
                                                        : "—"] }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "season span" })] })] }), _jsxs(Stack, { direction: "row", spacing: 3, sx: { mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }, divider: _jsx(Divider, { orientation: "vertical", flexItem: true }), justifyContent: "center", children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "body2", fontWeight: 500, children: "Regular Season" }), _jsxs(Typography, { variant: "caption", color: "text.secondary", children: [gameWeeks.length, " weeks \u2022 ", totalGames, " matches"] })] }), playoffWeeks.length > 0 && (_jsxs(Box, { children: [_jsx(Typography, { variant: "body2", fontWeight: 500, sx: { color: "#D4AF37" }, children: "Playoffs" }), _jsxs(Typography, { variant: "caption", color: "text.secondary", children: [playoffWeeks.length, " weeks \u2022 ", totalPlayoffMatches, " matches"] })] }))] })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", sx: { mb: 2 }, children: [_jsx(IconButton, { onClick: goToPrevMonth, size: "small", children: _jsx(NavigateBeforeIcon, {}) }), _jsx(Typography, { variant: "h6", fontWeight: 600, children: format(currentMonth, "MMMM yyyy") }), _jsx(IconButton, { onClick: goToNextMonth, size: "small", children: _jsx(NavigateNextIcon, {}) })] }), _jsx(Box, { sx: {
                                    display: "grid",
                                    gridTemplateColumns: "repeat(7, 1fr)",
                                    gap: 0.5,
                                    mb: 1,
                                }, children: getDayNames(gameWeekStartDay).map(function (day) { return (_jsx(Typography, { variant: "caption", fontWeight: 600, color: "text.secondary", sx: { textAlign: "center", py: 0.5 }, children: day }, day)); }) }), _jsx(Box, { sx: {
                                    display: "grid",
                                    gridTemplateColumns: "repeat(7, 1fr)",
                                    gap: 0.5,
                                }, children: calendarDays.map(function (day, index) {
                                    var isCurrentMonth = isSameMonth(day, currentMonth);
                                    var dayOfWeek = getDay(day);
                                    var isPlayDay = enabledDays.has(dayOfWeek);
                                    var exclusion = isDateExcluded(day);
                                    var wholeWeekExclusion = isInWholeWeekExclusion(day);
                                    var blackout = isInBlackout(day);
                                    var gameWeek = getGameWeekForDate(day);
                                    var playoffWeek = getPlayoffWeekForDate(day);
                                    var isExcluded = exclusion || wholeWeekExclusion || blackout;
                                    var isToday = isSameDay(day, new Date());
                                    var showGameWeekBar = gameWeek && isPlayDay && !isExcluded;
                                    var showPlayoffBar = playoffWeek && isPlayDay && !isExcluded && !gameWeek;
                                    var maxPlayoffRounds = playoffWeeks.length;
                                    var tooltipContent = "";
                                    if (exclusion && !exclusion.wholeWeek) {
                                        tooltipContent = "Excluded: ".concat(exclusion.reason);
                                    }
                                    else if (wholeWeekExclusion) {
                                        tooltipContent = "Week excluded: ".concat(wholeWeekExclusion.reason);
                                    }
                                    else if (blackout) {
                                        tooltipContent = "Blackout: ".concat(blackout.reason);
                                    }
                                    else if (playoffWeek) {
                                        tooltipContent = "Playoffs: ".concat(getPlayoffRoundName(playoffWeek.roundNumber, maxPlayoffRounds));
                                    }
                                    else if (gameWeek) {
                                        tooltipContent = "Gameweek ".concat(gameWeek.weekNumber);
                                    }
                                    return (_jsx(Tooltip, { title: tooltipContent, arrow: true, children: _jsxs(Box, { sx: {
                                                aspectRatio: "1",
                                                display: "flex",
                                                flexDirection: "column",
                                                borderRadius: 1,
                                                border: 1,
                                                borderColor: isToday ? "secondary.main" : "divider",
                                                borderWidth: isToday ? 2 : 1,
                                                position: "relative",
                                                cursor: tooltipContent ? "help" : "default",
                                                overflow: "hidden",
                                            }, children: [_jsxs(Box, { sx: { minHeight: 24, position: "relative" }, children: [showGameWeekBar && (_jsx(Box, { sx: {
                                                                position: "absolute",
                                                                top: 2,
                                                                left: 2,
                                                                right: 2,
                                                                height: 20,
                                                                bgcolor: "primary.main",
                                                                borderRadius: 0.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }, children: _jsxs(Typography, { variant: "caption", sx: {
                                                                    color: "white",
                                                                    fontWeight: 700,
                                                                    fontSize: "0.7rem",
                                                                    lineHeight: 1,
                                                                    whiteSpace: "nowrap",
                                                                }, children: ["GW", gameWeek.weekNumber] }) })), showPlayoffBar && (_jsx(Box, { sx: {
                                                                position: "absolute",
                                                                top: 2,
                                                                left: 2,
                                                                right: 2,
                                                                height: 20,
                                                                bgcolor: "#D4AF37",
                                                                borderRadius: 0.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }, children: _jsx(Typography, { variant: "caption", sx: {
                                                                    color: "white",
                                                                    fontWeight: 700,
                                                                    fontSize: "0.65rem",
                                                                    lineHeight: 1,
                                                                    whiteSpace: "nowrap",
                                                                }, children: getPlayoffRoundName(playoffWeek.roundNumber, maxPlayoffRounds) }) })), isExcluded && (_jsx(Box, { sx: {
                                                                position: "absolute",
                                                                top: 2,
                                                                left: 2,
                                                                right: 2,
                                                                height: 20,
                                                                bgcolor: "error.main",
                                                                borderRadius: 0.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }, children: _jsx(Typography, { variant: "caption", sx: {
                                                                    color: "white",
                                                                    fontWeight: 600,
                                                                    fontSize: "0.5rem",
                                                                    lineHeight: 1,
                                                                    whiteSpace: "nowrap",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }, children: "Off" }) }))] }), _jsx(Box, { sx: {
                                                        flex: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        pb: 0.5,
                                                    }, children: _jsx(Typography, { variant: "body2", fontWeight: isToday ? 700 : 400, color: isCurrentMonth ? "text.primary" : "text.disabled", children: format(day, "d") }) })] }) }, index));
                                }) }), _jsxs(Stack, { direction: "row", spacing: 2, sx: { mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }, flexWrap: "wrap", children: [_jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 12,
                                                    bgcolor: "primary.main",
                                                    borderRadius: 0.5,
                                                } }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Gameweek" })] }), playoffWeeks.length > 0 && (_jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 12,
                                                    bgcolor: "#D4AF37",
                                                    borderRadius: 0.5,
                                                } }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Playoffs" })] })), _jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 24,
                                                    height: 12,
                                                    bgcolor: "error.main",
                                                    borderRadius: 0.5,
                                                } }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Excluded" })] }), _jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", children: [_jsx(Box, { sx: {
                                                    width: 16,
                                                    height: 16,
                                                    border: 2,
                                                    borderColor: "secondary.main",
                                                    borderRadius: 0.5,
                                                } }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: "Today" })] })] })] }), _jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3 }, children: [_jsx(Typography, { variant: "subtitle2", fontWeight: 600, sx: { mb: 2 }, children: "Gameweek Schedule" }), _jsxs(Box, { sx: {
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }, children: [gameWeeks.slice(0, 20).map(function (gw) { return (_jsx(Chip, { label: "GW".concat(gw.weekNumber, ": ").concat(format(gw.weekStart, "MMM d")), size: "small", color: "primary", variant: "outlined", onClick: function () { return setCurrentMonth(gw.weekStart); } }, gw.weekNumber)); }), gameWeeks.length > 20 && (_jsx(Chip, { label: "+".concat(gameWeeks.length - 20, " more"), size: "small", variant: "outlined" }))] })] }), (excludedDates.length > 0 || blackoutPeriods.length > 0) && (_jsxs(Paper, { variant: "outlined", sx: { p: 2, mb: 3, bgcolor: "error.lighter" }, children: [_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { mb: 1 }, children: [_jsx(EventBusyIcon, { color: "error", fontSize: "small" }), _jsx(Typography, { variant: "subtitle2", fontWeight: 600, color: "error.main", children: "Exclusions Applied" })] }), _jsxs(Stack, { direction: "row", spacing: 1, sx: { flexWrap: "wrap", gap: 1 }, children: [excludedDates.map(function (ed, i) { return (_jsx(Chip, { size: "small", label: ed.wholeWeek
                                            ? "Week of ".concat(format(ed.date, "MMM d"), " (").concat(ed.reason, ")")
                                            : "".concat(format(ed.date, "MMM d"), " (").concat(ed.reason, ")"), color: "error", variant: "outlined" }, i)); }), blackoutPeriods.map(function (bp, i) { return (_jsx(Chip, { size: "small", label: "".concat(format(bp.startDate, "MMM d"), "\u2013").concat(format(bp.endDate, "MMM d"), " (").concat(bp.reason, ")"), color: "error", variant: "outlined" }, "bp-".concat(i))); })] })] }))] })), generateError && (_jsx(Alert, { severity: "error", sx: { mb: 3 }, children: generateError })), !isReady && (_jsxs(Alert, { severity: "warning", sx: { mb: 3 }, children: [_jsx(Typography, { variant: "body2", fontWeight: 500, gutterBottom: true, children: scheduleMode === "manual"
                            ? "Cannot activate season yet:"
                            : "Cannot generate fixtures yet:" }), _jsxs(Stack, { component: "ul", sx: { m: 0, pl: 2 }, spacing: 0.5, children: [teamCount < 2 && (_jsx(Typography, { component: "li", variant: "body2", children: "Need at least 2 teams assigned to divisions" })), scheduleMode !== "manual" && !(formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.matchesPerPairing) && (_jsx(Typography, { component: "li", variant: "body2", children: "Match format not configured" })), scheduleMode !== "manual" && !(matchTiming === null || matchTiming === void 0 ? void 0 : matchTiming.matchDuration) && (_jsx(Typography, { component: "li", variant: "body2", children: "Match timing not configured" })), scheduleMode !== "manual" && !(formatRounds === null || formatRounds === void 0 ? void 0 : formatRounds.startDate) && (_jsx(Typography, { component: "li", variant: "body2", children: "Season start date not set" })), scheduleMode !== "manual" && !hasSchedule && (_jsx(Typography, { component: "li", variant: "body2", children: "No play days configured in schedule" })), scheduleMode !== "manual" &&
                                gameWeeks.length === 0 &&
                                hasSchedule && (_jsx(Typography, { component: "li", variant: "body2", children: "Unable to calculate gameweeks" }))] })] })), _jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(Button, { variant: "contained", size: "large", color: "success", disabled: !isReady || isGenerating, onClick: onGenerate, startIcon: isGenerating ? (_jsx(CircularProgress, { size: 20, color: "inherit" })) : (_jsx(CheckCircleIcon, {})), sx: { px: 6, py: 1.5, fontSize: "1.1rem" }, children: isGenerating
                        ? scheduleMode === "manual"
                            ? "Activating..."
                            : "Generating..."
                        : scheduleMode === "manual"
                            ? "Activate Season"
                            : "Generate Fixtures" }) }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { display: "block", textAlign: "center", mt: 2 }, children: scheduleMode === "manual" ? (_jsxs(_Fragment, { children: ["This will activate the season with no matches.", _jsx("br", {}), "You can create matches individually from the season page."] })) : (_jsxs(_Fragment, { children: ["This will create all ", totalGames + totalPlayoffMatches, " matches for the season.", _jsx("br", {}), "You can edit individual matches after generation."] })) })] }));
};
